import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ProductDto } from "@gaole/shared";
import { api, ApiError } from "../lib/api";
import { rm, timeAgo } from "../lib/format";
import { getSocket } from "../lib/socket";
import { useAuth } from "../stores/auth";
import { toast } from "../stores/toast";
import { ProductHeroImage, heroCaptionTextClass } from "../components/ProductCard";
import { Countdown } from "../components/Countdown";
import { Badge, Button, Input, PageLoader, statusColor } from "../components/ui";

interface AuctionDetail {
  id: string;
  title: string;
  description: string | null;
  status: string;
  startPrice: number;
  reservePrice: number | null;
  reserveMet: boolean;
  buyNowPrice: number | null;
  currentPrice: number;
  minNextBid: number;
  bidCount: number;
  startsAt: string;
  endsAt: string;
  antiSnipeMinutes: number;
  isLeading: boolean;
  myMaxBid: number | null;
  winnerUserId: string | null;
  isWinner: boolean;
  orderId: string | null;
  bids: { id: string; amount: number; bidderName: string; isProxy: boolean; at: string }[];
  product: ProductDto;
}

export function AuctionRoom() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [amount, setAmount] = useState("");
  const [placing, setPlacing] = useState(false);
  const [flash, setFlash] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["auction", id],
    queryFn: () => api<{ auction: AuctionDetail }>(`/auctions/${id}`),
  });

  useEffect(() => {
    if (!id) return;
    const socket = getSocket();
    socket.emit("auction:join", id);
    const invalidate = () => {
      setFlash(true);
      setTimeout(() => setFlash(false), 700);
      void queryClient.invalidateQueries({ queryKey: ["auction", id] });
    };
    socket.on("auction:bid", invalidate);
    socket.on("auction:extended", invalidate);
    socket.on("auction:ended", invalidate);
    return () => {
      socket.emit("auction:leave", id);
      socket.off("auction:bid", invalidate);
      socket.off("auction:extended", invalidate);
      socket.off("auction:ended", invalidate);
    };
  }, [id, queryClient]);

  if (isLoading) return <PageLoader />;
  if (!data) return null;
  const auction = data.auction;

  async function placeBid() {
    if (!user) {
      navigate("/login");
      return;
    }
    const value = Number(amount);
    if (!Number.isFinite(value) || value < auction.minNextBid) {
      toast("error", `Minimum bid is ${rm(auction.minNextBid)}`);
      return;
    }
    setPlacing(true);
    try {
      const res = await api<{ youAreLeading: boolean; currentPrice: number; extended: boolean }>(
        `/auctions/${auction.id}/bid`,
        { body: { maxAmount: value } },
      );
      toast(
        res.youAreLeading ? "success" : "info",
        res.youAreLeading
          ? "You're the highest bidder!"
          : `Outbid instantly — price is now ${rm(res.currentPrice)}`,
      );
      setAmount("");
      await queryClient.invalidateQueries({ queryKey: ["auction", id] });
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Bid failed");
    } finally {
      setPlacing(false);
    }
  }

  async function buyNow() {
    if (!user) {
      navigate("/login");
      return;
    }
    setPlacing(true);
    try {
      const res = await api<{ orderId: string }>(`/auctions/${auction.id}/buy-now`, { body: {} });
      toast("success", "Lot secured — complete payment to claim");
      navigate(`/checkout/order/${res.orderId}`);
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Buy now failed");
    } finally {
      setPlacing(false);
    }
  }

  return (
    <div className="min-w-0 max-w-full space-y-5 px-4 pt-4">
      <div className="relative w-full min-w-0 max-w-full overflow-hidden rounded-3xl border border-white/5">
        <ProductHeroImage src={auction.product.images[0] ?? null} aspectClassName="aspect-[4/3]" overlay="full" />
        <div className="absolute left-3 top-3 z-30 flex gap-2">
          <Badge color={statusColor(auction.status)}>{auction.status}</Badge>
          {auction.isLeading && <Badge color="green">You're leading</Badge>}
        </div>
        <div className={`absolute bottom-0 left-0 right-0 z-30 p-4 ${heroCaptionTextClass}`}>
          <h1 className="font-display text-lg font-bold leading-tight">{auction.title}</h1>
        </div>
      </div>

      {auction.description && <p className="text-xs leading-relaxed text-white/60">{auction.description}</p>}

      <div className={`rounded-2xl border bg-surface p-4 transition-colors duration-500 ${flash ? "border-brand-pink" : "border-white/5"}`}>
        <div className="flex items-end justify-between">
          <div>
            <p className="text-[11px] text-white/50">{auction.bidCount > 0 ? "Current bid" : "Starting at"}</p>
            <p className="font-display text-2xl font-bold text-brand-pink">
              {rm(auction.bidCount > 0 ? auction.currentPrice : auction.startPrice)}
            </p>
            {auction.reservePrice != null && (
              <p className={`text-[10px] ${auction.reserveMet ? "text-emerald-400" : "text-amber-400"}`}>
                {auction.reserveMet ? "Reserve met" : "Reserve not met"}
              </p>
            )}
          </div>
          <div className="text-right">
            {auction.status === "LIVE" ? (
              <>
                <p className="text-[11px] text-white/50">Ends in</p>
                <Countdown target={auction.endsAt} onDone={() => queryClient.invalidateQueries({ queryKey: ["auction", id] })} />
              </>
            ) : auction.status === "SCHEDULED" ? (
              <>
                <p className="text-[11px] text-white/50">Starts in</p>
                <Countdown target={auction.startsAt} onDone={() => queryClient.invalidateQueries({ queryKey: ["auction", id] })} />
              </>
            ) : (
              <p className="text-xs text-white/50">{auction.bidCount} bids</p>
            )}
          </div>
        </div>

        {auction.status === "LIVE" && (
          <div className="mt-4 space-y-2">
            {auction.myMaxBid != null && (
              <p className="text-center text-[11px] text-white/50">
                Your max bid: <span className="font-semibold text-white/80">{rm(auction.myMaxBid)}</span>
                {auction.isLeading ? " · leading" : " · outbid"}
              </p>
            )}
            <div className="flex gap-2">
              <Input
                type="number"
                inputMode="decimal"
                min={auction.minNextBid}
                step="0.01"
                placeholder={`Max bid — min ${rm(auction.minNextBid)}`}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              <Button disabled={placing} onClick={placeBid}>
                Bid
              </Button>
            </div>
            <p className="text-center text-[10px] text-white/40">
              Proxy bidding: we bid the minimum needed up to your max. Bids in the last{" "}
              {auction.antiSnipeMinutes} min extend the auction.
            </p>
            {auction.buyNowPrice != null && auction.bidCount === 0 && (
              <Button variant="outline" className="w-full" disabled={placing} onClick={buyNow}>
                Buy now · {rm(auction.buyNowPrice)}
              </Button>
            )}
          </div>
        )}

        {["ENDED", "COMPLETED"].includes(auction.status) && (
          <div className="mt-4 space-y-2 text-center">
            <p className="font-display text-sm font-bold">
              {auction.isWinner
                ? "You won this lot!"
                : auction.winnerUserId
                  ? `Sold for ${rm(auction.currentPrice)}`
                  : "Ended without a winner"}
            </p>
            {auction.isWinner && auction.orderId && (
              <Link to={`/checkout/order/${auction.orderId}`}>
                <Button className="w-full">Complete payment →</Button>
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Bid history */}
      <section className="space-y-2">
        <h2 className="font-display text-xs font-semibold uppercase tracking-widest text-white/40">
          Bid history
        </h2>
        {auction.bids.length === 0 ? (
          <p className="text-xs text-white/40">No bids yet — be the first.</p>
        ) : (
          <div className="space-y-1.5">
            {auction.bids.map((bid, i) => (
              <div
                key={bid.id}
                className={`flex items-center justify-between rounded-xl px-3 py-2 text-xs ${
                  i === 0 ? "bg-brand-pink/10 text-white" : "bg-surface/60 text-white/60"
                }`}
              >
                <span className="font-medium">
                  {bid.bidderName}
                  {bid.isProxy && <span className="ml-1 text-[9px] text-white/35">auto</span>}
                </span>
                <span className="flex items-center gap-2">
                  <span className={i === 0 ? "font-bold text-brand-pink" : ""}>{rm(bid.amount)}</span>
                  <span className="text-[10px] text-white/35">{timeAgo(bid.at)}</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
